import { ComponentType, useMemo } from "react";

import { EntityId } from "@reduxjs/toolkit";

import {
    Grid,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Switch,
} from "@mui/material";

import { useAppSelector } from "app/hooks";
import { DeviceThumbnail } from "./DeviceThumbnail";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { MatchType } from "features/appFeatures/types/matchType";
import { AppFeature } from "features/appFeatures/types/AppFeature";

import {
    DeviceListVisibilityToggle,
    DeviceListVisibilityToggleProps,
} from "./DeviceListVisibilityToggle";
import { DeviceListItem } from "features/devices/DeviceListItem";

export enum DeviceListToggleMode {
    Toggle,
    Visibility,
}

export interface DeviceListProps {
    devices: DeviceListItem[];
    selectedDeviceIds?: EntityId[];
    onToggleDevice: (deviceId: EntityId, selected: boolean) => void;
    thumbnailRefreshIntervalInMs: number;
    onToggleSelectAll?: (selected: boolean) => void;
    selectAllToggleLabel?: string;
    columnCount?: number;

    hideDeviceThumbnail?: boolean;
    deviceListToggleMode?: DeviceListToggleMode;
    deviceListVisibilityToggleComponent: ComponentType<DeviceListVisibilityToggleProps>;
}

export function DeviceList(props: DeviceListProps) {
    const deviceListToggleMode =
        props.deviceListToggleMode ?? DeviceListToggleMode.Toggle;

    const displayErrors = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(
            state,
            [AppFeature.AdminDebugTools],
            MatchType.All,
        ),
    );

    const columnSize = 12 / (props.columnCount ?? 1);

    const devicesChunks = useMemo(() => {
        const columnCount = props.columnCount ?? 1;
        const devices = props.devices ?? [];

        return [...Array(columnCount).keys()].map((c) =>
            devices.filter((_, i) => i % columnCount === c),
        );
    }, [props.columnCount, props.devices]);

    return (
        <Grid
            container
            spacing={0}
        >
            {props.onToggleSelectAll && (
                <>
                    <Grid size={12}>
                        <Grid size={columnSize}>
                            <ListItem key="select-all">
                                <ListItemText
                                    id="switch-list-label-all"
                                    primary={
                                        props.selectedDeviceIds &&
                                        props.selectedDeviceIds?.length > 0
                                            ? "Deselect all"
                                            : (props.selectAllToggleLabel ??
                                              "Select all")
                                    }
                                />
                                {deviceListToggleMode ===
                                    DeviceListToggleMode.Toggle && (
                                    <Switch
                                        edge="end"
                                        onChange={(e, checked) =>
                                            props.onToggleSelectAll &&
                                            props.onToggleSelectAll(checked)
                                        }
                                        checked={
                                            !!props.selectedDeviceIds?.length
                                        }
                                        inputProps={{
                                            "aria-labelledby":
                                                "switch-list-label-all",
                                        }}
                                    />
                                )}
                                {deviceListToggleMode ===
                                    DeviceListToggleMode.Visibility && (
                                    <DeviceListVisibilityToggle
                                        isSelected={
                                            !!props.selectedDeviceIds?.length
                                        }
                                        onChange={(checked) =>
                                            props.onToggleSelectAll &&
                                            props.onToggleSelectAll(checked)
                                        }
                                    />
                                )}
                            </ListItem>
                        </Grid>
                    </Grid>
                </>
            )}
            {devicesChunks &&
                devicesChunks.map((devicesChunk, i) => (
                    <Grid
                        size={columnSize}
                        key={`devicesChunk-${i}`}
                    >
                        {devicesChunk.map((device) => (
                            <ListItem key={device.id}>
                                {!props.hideDeviceThumbnail && (
                                    <ListItemAvatar sx={{ paddingRight: 2 }}>
                                        <DeviceThumbnail
                                            deviceId={device.id}
                                            deviceName={device.name}
                                            displayErrors={displayErrors}
                                            refreshIntervalInMs={
                                                props.thumbnailRefreshIntervalInMs
                                            }
                                        />
                                    </ListItemAvatar>
                                )}
                                <ListItemText
                                    id={`switch-list-label-${device.id}`}
                                    primary={device.name}
                                />
                                {deviceListToggleMode ===
                                    DeviceListToggleMode.Toggle && (
                                    <Switch
                                        edge="end"
                                        onChange={(e, checked) =>
                                            props.onToggleDevice(
                                                device.id,
                                                checked,
                                            )
                                        }
                                        checked={
                                            props.selectedDeviceIds?.some(
                                                (x) => x === device.id,
                                            ) || false
                                        }
                                        inputProps={{
                                            "aria-labelledby": `switch-list-label-${device.id}`,
                                        }}
                                    />
                                )}
                                {deviceListToggleMode ===
                                    DeviceListToggleMode.Visibility && (
                                    <props.deviceListVisibilityToggleComponent
                                        deviceId={device.id}
                                        isSelected={
                                            props.selectedDeviceIds?.some(
                                                (x) => x === device.id,
                                            ) || false
                                        }
                                        onChange={(checked) =>
                                            props.onToggleDevice(
                                                device.id,
                                                checked,
                                            )
                                        }
                                    />
                                )}
                            </ListItem>
                        ))}
                    </Grid>
                ))}
        </Grid>
    );
}
